// webapp/src/lib/lazyReload.ts
//
// Stale-chunk-post-deploy auto-recovery for React.lazy() route/component splits.
//
// Sentry SWEATSTUDIO-REACT-W (prod, Safari 17.6): a client on an old build
// (v1.68.2 while prod ~v1.73) lazy-imports a hashed chunk that later deploys
// have purged. Cloudflare Pages' SPA fallback serves index.html (Content-Type
// text/html, 200) for the missing asset → the browser refuses to execute HTML
// as an ES module → `TypeError: 'text/html' is not a valid JavaScript MIME type`
// → the import() rejects → React.lazy propagates → ErrorBoundary catches, user
// stuck. The fix: on a chunk-load error, hard-reload ONCE to fetch the current
// build, guarded so a genuinely-broken build cannot reload-loop.

import { lazy, type ComponentType } from 'react'

// sessionStorage (per-tab) loop-guard: set before the reload, cleared on a
// successful import. Per-tab so a reload in one tab doesn't suppress recovery
// in another.
const RELOAD_FLAG = 'ss:chunk-reload'

// sessionStorage can throw (Safari "block all cookies" / privacy modes) — a
// throw on the SUCCESS path would turn a good load into a fake error, so every
// access is guarded. ponytail: if storage is fully blocked the guard no-ops
// (getFlag→null), so a genuinely-broken deploy could reload-loop in that mode;
// acceptable — a stale chunk still self-heals in one reload, and admin routes
// (where this fired) already require working storage for the Supabase session.
function getFlag(): string | null {
  try {
    return window.sessionStorage.getItem(RELOAD_FLAG)
  } catch {
    return null
  }
}
function setFlag(): void {
  try {
    window.sessionStorage.setItem(RELOAD_FLAG, '1')
  } catch {
    /* storage blocked — nothing to persist */
  }
}
function clearFlag(): void {
  try {
    window.sessionStorage.removeItem(RELOAD_FLAG)
  } catch {
    /* storage blocked — nothing to clear */
  }
}

// The dynamic-import / chunk-load failure family across browsers:
//  - Safari:          "'text/html' is not a valid JavaScript MIME type"  (stale chunk → SPA index.html)
//  - Chrome/Firefox:  "Failed to fetch dynamically imported module"
//  - Firefox alt:     "error loading dynamically imported module"
//  - Safari alt:      "Importing a module script failed"
//  - webpack-era:     err.name === 'ChunkLoadError'
export function isChunkLoadError(err: unknown): boolean {
  const message = err instanceof Error ? err.message : String(err ?? '')
  const name = err instanceof Error ? err.name : ''
  return (
    name === 'ChunkLoadError' ||
    /not a valid JavaScript MIME type/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) ||
    /Importing a module script failed/i.test(message)
  )
}

// Returns true if a recovery reload was triggered (caller should then stall on a
// never-resolving promise, since the document is about to be replaced). Returns
// false if this is not a chunk error, or the guard is already set (already
// reloaded once → genuine break → let it propagate to the ErrorBoundary).
export function attemptChunkReload(err: unknown): boolean {
  if (isChunkLoadError(err) && !getFlag()) {
    setFlag()
    window.location.reload()
    return true
  }
  return false
}

// Drop-in replacement for React.lazy that auto-recovers from stale chunks.
// Mirrors React.lazy's own generic (`T extends ComponentType<any>`) so typed
// component props are preserved at call sites.
// ponytail: clear-on-success re-arms recovery for a later deploy within the same
// long-lived session; the tradeoff is a broken chunk hidden behind a working one
// could reload twice — acceptably rare vs. leaving stale tabs permanently broken.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) {
  return lazy(async () => {
    try {
      const mod = await factory()
      clearFlag()
      return mod
    } catch (err) {
      if (attemptChunkReload(err)) {
        // Reload in flight — never resolve, so the error fallback never flashes
        // before the document is replaced by the fresh build.
        return new Promise<{ default: T }>(() => {})
      }
      throw err
    }
  })
}
